import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

const components: Components = {
  h1: ({ children }) => <h1 className="mt-2 mb-1 text-lg font-bold">{children}</h1>,
  h2: ({ children }) => <h2 className="mt-2 mb-1 text-base font-bold">{children}</h2>,
  h3: ({ children }) => <h3 className="mt-1 mb-0.5 text-sm font-bold">{children}</h3>,
  p: ({ children }) => <p className="mt-0.5 text-sm text-gray-800 dark:text-gray-200">{children}</p>,
  ul: ({ children }) => <ul className="ml-4 list-disc text-sm text-gray-800 dark:text-gray-200">{children}</ul>,
  ol: ({ children }) => <ol className="ml-4 list-decimal text-sm text-gray-800 dark:text-gray-200">{children}</ol>,
  li: ({ children }) => <li className="mt-0.5">{children}</li>,
  blockquote: ({ children }) => (
    <blockquote className="my-1 border-l-2 border-gray-300 pl-3 italic text-gray-600 dark:border-gray-600 dark:text-gray-400">
      {children}
    </blockquote>
  ),
  code: ({ className, children }) => {
    const isBlock = className?.includes('language-');
    if (isBlock) {
      return (
        <code className={`block overflow-x-auto rounded bg-gray-100 p-3 text-xs dark:bg-gray-800 ${className ?? ''}`}>
          {children}
        </code>
      );
    }
    return (
      <code className="rounded bg-gray-100 px-1 py-0.5 text-xs dark:bg-gray-800">
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre className="my-1 overflow-x-auto rounded bg-gray-100 text-xs dark:bg-gray-800">{children}</pre>,
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-blue-600 underline hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
    >
      {children}
    </a>
  ),
  table: ({ children }) => (
    <div className="my-1 overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="border border-gray-300 bg-gray-50 px-2 py-1 text-left font-semibold dark:border-gray-600 dark:bg-gray-800">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-gray-300 px-2 py-1 dark:border-gray-600">{children}</td>
  ),
  hr: () => <hr className="my-2 border-gray-200 dark:border-gray-700" />,
};

interface MarkdownContentProps {
  content: string;
}

/** Replace @[Display Name] and @slug with styled <span> after markdown rendering */
function renderWithMentions(text: string): React.ReactNode[] {
  // Match @[Bracketed Name] or @word — the lookbehind stops email addresses
  // from rendering their domain as a mention ("bill@example.com" previously
  // showed "@example.com" as a blue pill)
  const mentionRegex = /@\[([^\]]+)\]|(?<![\w.])@(\w[\w.]*)/g;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  while ((match = mentionRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const displayName = match[1] ?? match[2]; // bracketed or single-word
    parts.push(
      <span key={match.index} className="mention-highlight rounded-sm bg-blue-100 px-0.5 font-medium text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
        @{displayName}
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [text];
}

/** Wrap a markdown component to process mentions in its text children */
function withMentions<P extends { children?: React.ReactNode }>(Component: React.ComponentType<P>) {
  return function MentionWrapper(props: P) {
    const processed = processChildren(props.children);
    return <Component {...props}>{processed}</Component>;
  };
}

function processChildren(children: React.ReactNode): React.ReactNode {
  if (typeof children === 'string') return renderWithMentions(children);
  if (Array.isArray(children)) return children.map((c, i) => <React.Fragment key={i}>{processChildren(c)}</React.Fragment>);
  if (children && typeof children === 'object' && 'props' in children) {
    // React element — process its children recursively
    const el = children as React.ReactElement<{ children?: React.ReactNode }>;
    if (el.props.children) {
      return React.cloneElement(el, {}, processChildren(el.props.children));
    }
  }
  return children;
}

const mentionComponents: Components = {
  ...components,
  p: withMentions(components.p as React.ComponentType<{ children?: React.ReactNode }>),
  li: withMentions(components.li as React.ComponentType<{ children?: React.ReactNode }>),
};

export function MarkdownContent({ content }: MarkdownContentProps) {
  return (
    <div className="markdown-content">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mentionComponents}>
        {content}
      </ReactMarkdown>
    </div>
  );
}
